import { type ChangeEvent, type FC, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Typography } from "../typography/Typography";
import { IAmountDropdownProps } from "./InputDropdown.interface";
import { TypographyVariant } from "../typography/Typography.interface";
import { DropDownHeaderProps } from "../drop-down/Dropdown.interface";
import { CURRENCIES, CurrencyCodes } from "@fena/toolkit-types";
import { ArrowDownGrey, ArrowUpBlue, CheckBlue } from "../../icons";
import {
  CurrencyItem,
  defaultCurrencyItemForDropdown,
} from "../../../constants/currencies";
import { FlagIcon, FlagIconCode } from "react-flag-kit";
import { TextInputErrorVariant } from "../input-fields";

const borderVariants = {
  error: css`
    border-color: #ef6355;
  `,
  success: css`
    border-color: #2cd19e;
  `,
  default: css`
    border-color: #dbe3eb;
  `,
  active: css`
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  `,
};

const CheckIcon = styled(CheckBlue)`
  width: 10px;
`;

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const CategoryTitle = styled(Typography)`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: #13273f;
`;

const DropDownListContainer = styled.div`
  position: absolute;
  top: 43px;
  width: 100%;
  z-index: 99;
  border: 1px solid #eeeeee;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(129, 129, 165, 0.15);
`;

const DropDownList = styled.ul`
  box-sizing: border-box;
  padding: 0;
  background: #fff;
  margin: 3px 0;
`;

const Category = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 10px 12px;
  cursor: pointer;
  border-bottom: solid 1px #eeeeee;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f4f7f9;
  }
`;

const DropDownHeader = styled.div<DropDownHeaderProps>`
  width: 55px;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 22px;

  cursor: pointer;
  background-color: #f4f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #dbe3eb;
  border-right: none;
  border-radius: 5px 0 0 5px;
  padding: 6px 10px 6px 12px;
  min-height: 18px;
  position: relative;
  ${({ isOpen }) =>
    isOpen
      ? css`
          background-color: #ffffff;
          border: solid 1px rgba(2, 122, 255, 1);
          box-shadow: 0 0 0 3px rgba(2, 122, 255, 0.15);
        `
      : ""}
`;

const ValueSelect = styled.span`
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  text-align: center;
  color: #13273f;
  padding: 0 10px;
`;

const InputSection = styled.div`
  position: relative;
  width: 100%;
  height: 41px;
  display: flex;
  align-items: center;
  border-radius: 5px;
`;

const Search = styled.input<{ variant?: TextInputErrorVariant }>`
  width: 100%;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #13273f;
  padding: 7px 10px;
  box-sizing: border-box;
  background-color: #ffffff;
  border: 1px solid;
  border-radius: 0 5px 5px 0;
  outline: none;
  &:disabled {
    background-color: #f4f5f9;
  }

  ${({ variant }) => borderVariants[variant ?? TextInputErrorVariant.DEFAULT]};

  &:focus-within {
    /* padding-left: 9px; */
    z-index: 1;
    ${({ variant }) =>
      variant === TextInputErrorVariant.ERROR
        ? borderVariants[variant]
        : borderVariants[TextInputErrorVariant.ACTIVE]}
  }
`;

const Container = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledTypography = styled(Typography)`
  margin-bottom: 3px;
`;

const ErrorMessage = styled.span`
  font-family: Montserrat;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  color: #ef6355;
  margin-top: 5px;
`;

const FlagContainer = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const StyledFlagIcon = styled(FlagIcon)`
  height: 50%;
`;

const CountryFlagIcon: React.FunctionComponent<{
  code: FlagIconCode;
}> = ({ code }) => (
  <FlagContainer>
    <StyledFlagIcon code={code} />
  </FlagContainer>
);

export const AmountDropdown: FC<IAmountDropdownProps> = ({
  onChange,
  label,
  error,
  items,
  value,
  currencyValue,
  required,
  className,
  disabled,
  variant,
  onBlur,
  onDropdownChange,
  separateCurrencyDropdownChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [enteredValue, setEnteredValue] = useState<string>(value ?? "");
  const [currentCurrency, setCurrentCurrency] = useState(
    defaultCurrencyItemForDropdown
  );

  const ref = useRef(null);
  const valueRef = useRef(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const clickOnWindows = (e: MouseEvent) => {
      if (
        ref.current !== e.target &&
        e.target !== valueRef.current &&
        e.target !== searchRef.current
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener("click", clickOnWindows);

    return () => {
      window.removeEventListener("click", clickOnWindows);
    };
  }, []);

  const handleExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    !disabled && setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  };

  useEffect(() => {
    setEnteredValue(value ?? "");
  }, [value]);

  useEffect(() => {
    const foundCurrency =
      CURRENCIES[
        (currencyValue ?? CurrencyCodes.POUND_STERLING) as CurrencyCodes
      ];

    setCurrentCurrency(
      foundCurrency && currencyValue
        ? {
            label: foundCurrency.name,
            symbol: foundCurrency.symbol,
            currencyCode: currencyValue as CurrencyCodes,
            countryCode: foundCurrency.countryCode,
          }
        : defaultCurrencyItemForDropdown
    );
  }, [currencyValue]);

  const handleValue = (event: ChangeEvent<HTMLInputElement>) => {
    setEnteredValue(event.target.value);
    onChange({ value: event.target.value, currency: currentCurrency });
  };

  const handleBlurValue = () => {
    const updatedValue = parseFloat(enteredValue).toFixed(2).toString();
    if (!isNaN(+updatedValue)) {
      setEnteredValue(updatedValue);
      onChange({ value: updatedValue, currency: currentCurrency });
      onBlur?.();
    }
  };

  const handleItemClick = (currency: CurrencyItem) => () => {
    if (currentCurrency.currencyCode === currency.currencyCode) {
      setIsOpen(false);
      return;
    }
    onDropdownChange?.(currency);

    if (!separateCurrencyDropdownChange) {
      onChange({ currency, value: enteredValue ?? "" });
    }

    setIsOpen(false);
  };

  return (
    <Container className={className}>
      {label && (
        <StyledTypography variant={TypographyVariant.HEADER3}>
          {label}
          {required && (
            <span style={{ marginLeft: "3px", color: "red" }}>*</span>
          )}
        </StyledTypography>
      )}
      <InputSection>
        <DropDownHeader
          ref={ref}
          isOpen={isOpen}
          onClick={(e) => {
            if (items?.length) {
              handleExpand(e);
            }
          }}
        >
          <ValueSelect>{currentCurrency.symbol}</ValueSelect>
          {isOpen && !!items?.length ? (
            <ArrowUpBlue />
          ) : (
            !!items?.length && <ArrowDownGrey />
          )}
        </DropDownHeader>
        {isOpen && !!items?.length && (
          <DropDownListContainer
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <DropDownList>
              {items.map((item, index) => {
                return (
                  <div key={index}>
                    <Category onClick={handleItemClick(item)}>
                      <CategoryTitleWrapper>
                        <CountryFlagIcon
                          code={item.countryCode as FlagIconCode}
                        />
                        <CategoryTitle>{`${item?.label} (${item.symbol} ${item.currencyCode})`}</CategoryTitle>
                        <Typography
                          style={{ lineHeight: "15px" }}
                          variant={TypographyVariant.CAPTION}
                          color="#2CD19E"
                        >
                          {item.isDefaultCurrency ? "  (Default currency)" : ""}
                        </Typography>
                      </CategoryTitleWrapper>
                      {currentCurrency?.currencyCode === item?.currencyCode && (
                        <CheckIcon />
                      )}
                    </Category>
                  </div>
                );
              })}
            </DropDownList>
          </DropDownListContainer>
        )}

        <Search
          variant={variant}
          type="text"
          value={enteredValue}
          onChange={handleValue}
          onBlur={handleBlurValue}
          disabled={disabled}
        />
      </InputSection>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};
